/**
 * What j / k / y / n / i / x do to the Action Center, by acting on the page the
 * way a click would. Working from the DOM (rows carry `data-action-row`) keeps the
 * shortcuts exactly as strong as the buttons: a row that cannot be decided has no
 * Yes button, so `y` does nothing there.
 */

const ROW = '[data-action-row]';

export const rowsOnPage = (root: ParentNode = document): HTMLElement[] => Array.from(root.querySelectorAll<HTMLElement>(ROW));

export const focusedRow = (): HTMLElement | null => {
  const a = document.activeElement as HTMLElement | null;
  return a && a.closest ? (a.closest(ROW) as HTMLElement | null) : null;
};

/** Move the focus to the next / previous row (the first, when none is focused). Returns the row, or null when there are none. */
export function moveRow(dir: 1 | -1): HTMLElement | null {
  const rows = rowsOnPage();
  if (!rows.length) return null;
  const cur = focusedRow();
  const i = cur ? rows.indexOf(cur) : -1;
  const next = rows[Math.min(rows.length - 1, Math.max(0, i === -1 ? (dir === 1 ? 0 : rows.length - 1) : i + dir))];
  next.focus();
  if (typeof next.scrollIntoView === 'function') next.scrollIntoView({ block: 'nearest' });
  return next;
}

const buttonIn = (row: HTMLElement, label: string): HTMLButtonElement | null =>
  Array.from(row.querySelectorAll<HTMLButtonElement>('button')).find((b) => (b.textContent || '').trim() === label && !b.disabled) || null;

/** Press Yes / No / Instruct instead on the focused row. True when a button was pressed. */
export function pressInRow(label: 'Yes' | 'No' | 'Instruct instead'): boolean {
  const row = focusedRow();
  const b = row && buttonIn(row, label);
  if (!b) return false;
  b.click();
  return true;
}

/** Tick or untick the focused row for a bulk decision. True when there was a checkbox. */
export function toggleSelectInRow(): boolean {
  const row = focusedRow();
  const box = row && row.querySelector<HTMLInputElement>('input[type="checkbox"]');
  if (!box) return false;
  box.click();
  return true;
}
